/**
 * O banco de uma ficha no cliente: a réplica NDB sincronizada com o servidor, com escritas
 * otimistas e a conferência local de permissões.
 */
import type { NodeTree } from './tree.js';
import type { Replica } from './replica.js';
import type { NodeId, Op, OpBatch, Permission, TreeEvent } from './types.js';

/**
 * Autoridade local de uma réplica: diz, pela árvore que o cliente vê, se a pessoa
 * pode fazer o lote (o servidor confere de novo). Sem ela, tudo passa.
 */
export type ReplicaAuthority = {
  /** erro do primeiro op recusado, ou null */
  check(tree: NodeTree, ops: Op[]): string | null;
  can(tree: NodeTree, nodeId: NodeId, permission: Permission): boolean;
};

/**
 * Banco replicado: escritas otimistas, enviadas por `send`, confirmadas pelo servidor.
 *
 * A réplica avisa os ouvintes antes de devolver o lote, e um ouvinte (um campo calculado
 * reagindo à mudança) pode escrever de novo ali dentro. Por isso os lotes ficam numa caixa de
 * saída e só saem, na ordem em que foram criados, quando a escrita mais externa termina — senão
 * o servidor recebe a escrita num nó que ainda não existe para ele e recusa.
 */
export class ReplicaNodeDatabase {
  private readonly outbox: OpBatch[] = [];
  private depth = 0;

  constructor(
    readonly id: string,
    readonly replica: Replica,
    private readonly send: (batch: OpBatch) => void,
    private readonly authority: ReplicaAuthority | null = null,
  ) {}

  /** quem usa o cliente tem esta permissão no nó? */
  can(nodeId: NodeId, permission: Permission): boolean {
    return this.authority ? this.authority.can(this.replica.visible, nodeId, permission) : true;
  }

  /** o que o cliente vê agora (escritas locais incluídas) */
  visible(): NodeTree {
    return this.replica.visible;
  }

  /** o que o servidor já confirmou */
  persisted(): NodeTree {
    return this.replica.persisted;
  }

  newId(): NodeId {
    return this.replica.newId();
  }

  /** escrita local; lança se alguma operação não se aplica ou não é permitida */
  write(ops: Op[]): void {
    // recusa já o que o servidor recusaria (sem aparecer e sumir na tela)
    const denied = this.authority?.check(this.replica.visible, ops);
    if (denied) throw new Error(denied);
    this.depth += 1;
    try {
      this.outbox.push(this.replica.local(ops));
    } finally {
      this.depth -= 1;
    }
    if (this.depth > 0) return;
    const ready = this.outbox.splice(0).sort((a, b) => a.clientSeq - b.clientSeq);
    for (const batch of ready) this.send(batch);
  }

  /** mudanças na árvore visível (locais ou remotas) */
  subscribe(listener: (events: TreeEvent[], local: boolean) => void): () => void {
    return this.replica.on((change) => {
      if (change.layer === 'visible') listener(change.events, change.local);
    });
  }
}
